import React from 'react';
import { NextComponentType, NextPageContext } from 'next';

import { fetchCurrentUser } from '../redux/profiles/actions';
import { AuthService } from '../lib/services/auth-service';
import { RootState } from '../redux';
import { CurrentUser } from '../redux/profiles/interfaces';

/**
 * General HOC that allows redirection based on authentication. We should not
 * expose this: instead export specific routes and redirect combinations.
 */
const withAuthRedirect = (redirectRoute?: string) => <P,>(
  Page: NextComponentType<NextPageContext, {}, P>,
) => {
  return class WithAuthRedirect extends React.Component<P> {
    static async getInitialProps(ctx: NextPageContext) {
      const auth = new AuthService(ctx.req);
      const currentRoute = auth.currentRoute(ctx);

      if (!auth.isAuthenticated) {
        if (
          currentRoute === '/' ||
          currentRoute === '/signin' ||
          currentRoute === '/signup' ||
          currentRoute === '/search' ||
          currentRoute === '/reset-password' ||
          currentRoute === '/signup-individual' ||
          currentRoute === '/signup-corporate'
        )
          return {};

        auth.redirect(ctx, '/signin');

        return {};
      } else {
        const state: RootState = ctx.store.getState();
        const { currentUser = {} } = state.profiles;

        if ((currentUser as CurrentUser).userId === undefined) {
          // Fetch current user's profile
          ctx.store.dispatch(fetchCurrentUser());
        }

        if (redirectRoute) {
          auth.redirect(ctx, redirectRoute);
          return {};
        }
      }

      let initialProps = {};

      if (Page.getInitialProps) {
        initialProps = await Page.getInitialProps(ctx);
      }

      return {
        ...initialProps,
        isAuthenticated: auth.isAuthenticated,
      };
    }

    render() {
      return <Page {...this.props} />;
    }
  };
};

/**
 * HOC that redirects to login page if the user is not logged in.
 */
export const withLoginRedirect = withAuthRedirect();

/**
 * HOC that redirects to the dashboard if the user is logged in.
 */
export const withDashboardRedirect = withAuthRedirect('/dashboard');
